// src/components/EftposMachinesTab.tsx
//
// EFTPOS terminal inventory + vehicle assignment -- same shape as
// FuelCardsTab.tsx (pool of physical devices, junction table for
// assignment history), but with one deliberate difference: assigning a
// machine here also writes its Terminal ID into vehicles.eft_id, since
// that's the field used elsewhere to match settlement data back to a
// vehicle. Fuel Cards never touches the vehicles table -- this does, on
// purpose, per how the business actually uses eft_id.
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Badge } from "@/components/ui/badge";
import { Pencil, RefreshCw, Plus, Ban, Search, X, Link2, Unlink } from "lucide-react";

type EftposMachineStatus = "active" | "inactive";

type EftposMachineRow = {
  id: string;
  serial_number: string;
  terminal_id: string | null;
  provider: string | null;
  monthly_rental: number | null;
  network_provider: string | null;
  network_fee: number | null;
  status: EftposMachineStatus | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  vehicle_eftpos_machines?: Array<{
    vehicle_number: string;
    unassigned_at: string | null;
  }> | null;
};

type MachineFormValues = {
  serial_number: string;
  terminal_id: string;
  provider: string;
  monthly_rental: string;
  network_provider: string;
  network_fee: string;
  status: EftposMachineStatus;
  notes: string;
};

const EMPTY_FORM: MachineFormValues = {
  serial_number: "",
  terminal_id: "",
  provider: "Zylex",
  monthly_rental: "",
  network_provider: "",
  network_fee: "",
  status: "active",
  notes: "",
};

function statusBadgeVariant(status: string) {
  if (status === "active") return "default";
  return "secondary";
}

function stripSpaces(v: string): string {
  return v.replace(/\s+/g, "");
}

// Placeholder EFT ID for a vehicle with no machine (or a machine with no
// Terminal ID on file) -- matches the convention already used elsewhere in
// the fleet data, kept consistent so every vehicle's EFT ID is either a
// real Terminal ID or this sentinel, never null/blank.
const NO_MACHINE_EFT_ID = "0000";

function formatMoney(v: number | null): string {
  if (v === null || v === undefined) return "-";
  return `$${v.toFixed(2)}`;
}

// Natural sort - extracts numbers and sorts numerically, same convention as FuelCardsTab.
function naturalSort(a: string, b: string): number {
  const extractNumber = (str: string): number => {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };
  const numA = extractNumber(a);
  const numB = extractNumber(b);
  if (numA !== numB) return numA - numB;
  return a.localeCompare(b);
}

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees only the machine(s) assigned to their linked vehicle(s), and can
// never add/edit/assign/deactivate -- EFTPOS machines are a shared fleet
// resource, not something scoped to one vehicle.
const EftposMachinesTab: React.FC<{ isAdmin?: boolean; scopedVehicleNumbers?: string[] | null }> = ({ isAdmin = true, scopedVehicleNumbers = null }) => {
  const canManage = isAdmin && !scopedVehicleNumbers;
  const [machines, setMachines] = useState<EftposMachineRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [vehicleNumbers, setVehicleNumbers] = useState<string[]>([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "assigned" | "unassigned">("all");

  // Add new machine
  const [addOpen, setAddOpen] = useState(false);
  const [newMachine, setNewMachine] = useState<MachineFormValues>(EMPTY_FORM);

  // Edit machine
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<EftposMachineRow | null>(null);
  const [editValues, setEditValues] = useState<MachineFormValues>(EMPTY_FORM);

  // Assign machine to vehicle
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState<EftposMachineRow | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string>("");

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("eftpos_machines")
        .select(
          `
          id,
          serial_number,
          terminal_id,
          provider,
          monthly_rental,
          network_provider,
          network_fee,
          status,
          notes,
          created_at,
          updated_at,
          vehicle_eftpos_machines (
            vehicle_number,
            unassigned_at
          )
        `
        )
        .order("serial_number", { ascending: true });

      if (error) throw error;

      const cleaned = (Array.isArray(data) ? data : []).map((m: any) => {
        const vem = Array.isArray(m.vehicle_eftpos_machines) ? m.vehicle_eftpos_machines : [];
        const active = vem.filter((x: any) => x?.unassigned_at == null);
        return { ...m, vehicle_eftpos_machines: active };
      });

      setMachines(cleaned as EftposMachineRow[]);
    } catch (e: any) {
      toast({
        title: "Error fetching EFTPOS machines",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase.from("vehicles").select("vehicle_number");
      if (error) throw error;
      const numbers = (Array.isArray(data) ? data : []).map((v: any) => v.vehicle_number as string);
      numbers.sort(naturalSort);
      setVehicleNumbers(numbers);
    } catch (e: any) {
      toast({
        title: "Error fetching vehicles",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchMachines();
    fetchVehicles();
  }, []);

  const machinesWithAssignedVehicle = useMemo(() => {
    const mapped = (Array.isArray(machines) ? machines : []).map((m) => {
      const assignedVehicle =
        Array.isArray(m.vehicle_eftpos_machines) && m.vehicle_eftpos_machines.length > 0
          ? m.vehicle_eftpos_machines[0]?.vehicle_number
          : null;
      return { ...m, assignedVehicle };
    });
    if (!scopedVehicleNumbers) return mapped;
    const scoped = new Set(scopedVehicleNumbers);
    return mapped.filter((m) => m.assignedVehicle && scoped.has(m.assignedVehicle));
  }, [machines, scopedVehicleNumbers]);

  // vehicle_number -> machine currently on it, for the assign dialog's "already has a machine" hint
  const machineByVehicle = useMemo(() => {
    const map = new Map<string, EftposMachineRow>();
    for (const m of machinesWithAssignedVehicle) {
      if (m.assignedVehicle) map.set(m.assignedVehicle, m);
    }
    return map;
  }, [machinesWithAssignedVehicle]);

  const filteredAndSortedMachines = useMemo(() => {
    let result = [...machinesWithAssignedVehicle];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((m) => {
        return (
          m.serial_number.toLowerCase().includes(query) ||
          (m.terminal_id && m.terminal_id.toLowerCase().includes(query)) ||
          (m.provider && m.provider.toLowerCase().includes(query)) ||
          (m.notes && m.notes.toLowerCase().includes(query)) ||
          (m.assignedVehicle && m.assignedVehicle.toLowerCase().includes(query))
        );
      });
    }

    if (statusFilter !== "all") {
      result = result.filter((m) => m.status === statusFilter);
    }

    if (assignmentFilter === "assigned") {
      result = result.filter((m) => m.assignedVehicle !== null);
    } else if (assignmentFilter === "unassigned") {
      result = result.filter((m) => m.assignedVehicle === null);
    }

    result.sort((a, b) => naturalSort(a.serial_number, b.serial_number));

    return result;
  }, [machinesWithAssignedVehicle, searchQuery, statusFilter, assignmentFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setAssignmentFilter("all");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || statusFilter !== "all" || assignmentFilter !== "all";

  const resetAddForm = () => setNewMachine(EMPTY_FORM);

  const addMachine = async () => {
    if (!canManage) {
      toast({ title: "Not allowed", description: "Admin only.", variant: "destructive" });
      return;
    }
    if (!newMachine.serial_number.trim()) {
      toast({
        title: "Missing serial number",
        description: "Please enter a serial number for the machine.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("eftpos_machines").insert({
        serial_number: newMachine.serial_number.trim(),
        terminal_id: newMachine.terminal_id.trim() || null,
        provider: newMachine.provider.trim() || null,
        monthly_rental: newMachine.monthly_rental.trim() ? Number(newMachine.monthly_rental) : null,
        network_provider: newMachine.network_provider.trim() || null,
        network_fee: newMachine.network_fee.trim() ? Number(newMachine.network_fee) : null,
        status: newMachine.status,
        notes: newMachine.notes.trim() || null,
      });

      if (error) throw error;

      toast({ title: "EFTPOS machine added", description: `SN ${newMachine.serial_number} created.` });
      setAddOpen(false);
      resetAddForm();
      fetchMachines();
    } catch (e: any) {
      toast({
        title: "Error adding EFTPOS machine",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const openEdit = (machine: EftposMachineRow) => {
    if (!canManage) return;
    setEditing(machine);
    setEditValues({
      serial_number: machine.serial_number,
      terminal_id: machine.terminal_id || "",
      provider: machine.provider || "",
      monthly_rental: machine.monthly_rental != null ? String(machine.monthly_rental) : "",
      network_provider: machine.network_provider || "",
      network_fee: machine.network_fee != null ? String(machine.network_fee) : "",
      status: (machine.status as EftposMachineStatus) || "active",
      notes: machine.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!canManage || !editing) return;

    if (!editValues.serial_number.trim()) {
      toast({
        title: "Missing serial number",
        description: "Please enter a serial number.",
        variant: "destructive",
      });
      return;
    }

    try {
      const newTerminalId = editValues.terminal_id.trim() || null;

      const { error } = await supabase
        .from("eftpos_machines")
        .update({
          serial_number: editValues.serial_number.trim(),
          terminal_id: newTerminalId,
          provider: editValues.provider.trim() || null,
          monthly_rental: editValues.monthly_rental.trim() ? Number(editValues.monthly_rental) : null,
          network_provider: editValues.network_provider.trim() || null,
          network_fee: editValues.network_fee.trim() ? Number(editValues.network_fee) : null,
          status: editValues.status,
          notes: editValues.notes.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      // Keep a currently-assigned vehicle's eft_id in sync if the Terminal ID itself changed.
      const assignedVehicle = machineByVehicle.get(editing.assignedVehicle ?? "") ? editing.assignedVehicle : null;
      const currentAssignment = machinesWithAssignedVehicle.find((m) => m.id === editing.id)?.assignedVehicle;
      if (currentAssignment) {
        await supabase
          .from("vehicles")
          .update({ eft_id: newTerminalId ? stripSpaces(newTerminalId) : NO_MACHINE_EFT_ID, updated_at: new Date().toISOString() })
          .eq("vehicle_number", currentAssignment);
      }

      toast({ title: "EFTPOS machine updated", description: `SN ${editValues.serial_number} saved.` });
      setEditOpen(false);
      setEditing(null);
      fetchMachines();
    } catch (e: any) {
      toast({
        title: "Error updating EFTPOS machine",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const deactivateMachine = async (machine: EftposMachineRow) => {
    if (!canManage) return;

    const newStatus = machine.status === "active" ? "inactive" : "active";
    const action = newStatus === "inactive" ? "deactivated" : "reactivated";

    try {
      const { error } = await supabase
        .from("eftpos_machines")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", machine.id);

      if (error) throw error;

      toast({ title: `EFTPOS machine ${action}`, description: `SN ${machine.serial_number} is now ${newStatus}.` });
      fetchMachines();
    } catch (e: any) {
      toast({
        title: "Error updating status",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const openAssign = (machine: EftposMachineRow) => {
    if (!canManage) return;
    const current = machinesWithAssignedVehicle.find((m) => m.id === machine.id)?.assignedVehicle ?? "";
    setAssigning(machine);
    setSelectedVehicle(current);
    setAssignOpen(true);
  };

  const assignMachine = async () => {
    if (!canManage || !assigning) return;
    if (!selectedVehicle) {
      toast({ title: "Select a vehicle", description: "Choose which vehicle to assign this machine to.", variant: "destructive" });
      return;
    }

    try {
      // End any existing assignment for the target vehicle (whatever machine it currently has).
      await supabase
        .from("vehicle_eftpos_machines")
        .update({ unassigned_at: new Date().toISOString() })
        .eq("vehicle_number", selectedVehicle)
        .is("unassigned_at", null);

      // End any existing assignment for this machine (in case it's currently on a different vehicle).
      await supabase
        .from("vehicle_eftpos_machines")
        .update({ unassigned_at: new Date().toISOString() })
        .eq("eftpos_machine_id", assigning.id)
        .is("unassigned_at", null);

      const { error: insErr } = await supabase.from("vehicle_eftpos_machines").insert({
        vehicle_number: selectedVehicle,
        eftpos_machine_id: assigning.id,
        assigned_at: new Date().toISOString(),
      });
      if (insErr) throw insErr;

      // Populate the vehicle's EFT ID from this machine's Terminal ID for matching.
      const { error: vErr } = await supabase
        .from("vehicles")
        .update({
          eft_id: assigning.terminal_id ? stripSpaces(assigning.terminal_id) : NO_MACHINE_EFT_ID,
          updated_at: new Date().toISOString(),
        })
        .eq("vehicle_number", selectedVehicle);
      if (vErr) throw vErr;

      toast({
        title: "Machine assigned",
        description: assigning.terminal_id
          ? `SN ${assigning.serial_number} assigned to ${selectedVehicle} -- EFT ID updated.`
          : `SN ${assigning.serial_number} assigned to ${selectedVehicle}. No Terminal ID on file yet, so EFT ID wasn't set.`,
      });
      setAssignOpen(false);
      setAssigning(null);
      fetchMachines();
    } catch (e: any) {
      toast({
        title: "Error assigning machine",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const unassignMachine = async (machine: EftposMachineRow & { assignedVehicle?: string | null }) => {
    if (!canManage || !machine.assignedVehicle) return;

    const vehicleNumber = machine.assignedVehicle;

    try {
      const { error } = await supabase
        .from("vehicle_eftpos_machines")
        .update({ unassigned_at: new Date().toISOString() })
        .eq("vehicle_number", vehicleNumber)
        .eq("eftpos_machine_id", machine.id)
        .is("unassigned_at", null);
      if (error) throw error;

      // Only clear the vehicle's EFT ID if it still matches this exact machine -- don't
      // clobber a value that came from somewhere else in the meantime.
      if (machine.terminal_id) {
        const strippedTid = stripSpaces(machine.terminal_id);
        const { data: vRow } = await supabase
          .from("vehicles")
          .select("eft_id")
          .eq("vehicle_number", vehicleNumber)
          .maybeSingle();
        if (vRow?.eft_id === strippedTid) {
          await supabase
            .from("vehicles")
            .update({ eft_id: NO_MACHINE_EFT_ID, updated_at: new Date().toISOString() })
            .eq("vehicle_number", vehicleNumber);
        }
      }

      toast({ title: "Assignment removed", description: `SN ${machine.serial_number} unassigned from ${vehicleNumber}.` });
      fetchMachines();
    } catch (e: any) {
      toast({
        title: "Error removing assignment",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold">EFTPOS Machines</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchMachines} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Machine
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search machines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as "all" | "active" | "inactive")}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assignmentFilter} onValueChange={(val) => setAssignmentFilter(val as "all" | "assigned" | "unassigned")}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Assignment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Machines</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}

          {hasActiveFilters && (
            <span className="text-sm text-muted-foreground">
              Showing {filteredAndSortedMachines.length} of {machinesWithAssignedVehicle.length} machines
            </span>
          )}
        </div>

        <div className="rounded-md border max-h-[600px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_rgba(255,255,255,0.06)]">
              <TableRow>
                <TableHead>Serial Number</TableHead>
                <TableHead>Terminal ID</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Rental /mo</TableHead>
                <TableHead>Network</TableHead>
                <TableHead>Network Fee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned Vehicle</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    Loading EFTPOS machines...
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedMachines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters ? "No machines match the current filters." : "No EFTPOS machines found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedMachines.map((machine) => (
                  <TableRow key={machine.id}>
                    <TableCell className="font-medium">{machine.serial_number}</TableCell>
                    <TableCell className="font-mono">{machine.terminal_id || "-"}</TableCell>
                    <TableCell>{machine.provider || "-"}</TableCell>
                    <TableCell>{formatMoney(machine.monthly_rental)}</TableCell>
                    <TableCell>{machine.network_provider || "-"}</TableCell>
                    <TableCell>{formatMoney(machine.network_fee)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(machine.status)}>{machine.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {machine.assignedVehicle ? (
                        <Badge variant="outline">{machine.assignedVehicle}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Unassigned</span>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openAssign(machine)}
                            title="Assign to vehicle"
                          >
                            <Link2 className="h-4 w-4" />
                          </Button>
                          {machine.assignedVehicle && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => unassignMachine(machine)}
                              title="Unassign"
                            >
                              <Unlink className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEdit(machine)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${machine.status === "active" ? "text-destructive hover:text-destructive" : "text-green-600 hover:text-green-600"}`}
                            onClick={() => deactivateMachine(machine)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Add Machine Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New EFTPOS Machine</DialogTitle>
            <DialogDescription>Enter the details for the new machine.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="serial_number">Serial Number *</Label>
              <Input
                id="serial_number"
                value={newMachine.serial_number}
                onChange={(e) => setNewMachine({ ...newMachine, serial_number: e.target.value })}
                placeholder="e.g., 284 021 5921"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="terminal_id">Terminal ID</Label>
              <Input
                id="terminal_id"
                value={newMachine.terminal_id}
                onChange={(e) => setNewMachine({ ...newMachine, terminal_id: e.target.value })}
                placeholder="e.g., 614752 03"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="provider">Machine Provider</Label>
              <Input
                id="provider"
                value={newMachine.provider}
                onChange={(e) => setNewMachine({ ...newMachine, provider: e.target.value })}
                placeholder="e.g., Zylex"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="monthly_rental">Rental / Month ($)</Label>
                <Input
                  id="monthly_rental"
                  type="number"
                  step="0.01"
                  value={newMachine.monthly_rental}
                  onChange={(e) => setNewMachine({ ...newMachine, monthly_rental: e.target.value })}
                  placeholder="132.25"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="network_fee">Network Fee ($)</Label>
                <Input
                  id="network_fee"
                  type="number"
                  step="0.01"
                  value={newMachine.network_fee}
                  onChange={(e) => setNewMachine({ ...newMachine, network_fee: e.target.value })}
                  placeholder="19.43"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="network_provider">Network Provider</Label>
              <Input
                id="network_provider"
                value={newMachine.network_provider}
                onChange={(e) => setNewMachine({ ...newMachine, network_provider: e.target.value })}
                placeholder="e.g., Eftpos Central - WorldLine"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newMachine.status}
                onValueChange={(val) => setNewMachine({ ...newMachine, status: val as EftposMachineStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newMachine.notes}
                onChange={(e) => setNewMachine({ ...newMachine, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addMachine}>Add Machine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Machine Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit EFTPOS Machine</DialogTitle>
            <DialogDescription>Update the machine details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit_serial_number">Serial Number *</Label>
              <Input
                id="edit_serial_number"
                value={editValues.serial_number}
                onChange={(e) => setEditValues({ ...editValues, serial_number: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_terminal_id">Terminal ID</Label>
              <Input
                id="edit_terminal_id"
                value={editValues.terminal_id}
                onChange={(e) => setEditValues({ ...editValues, terminal_id: e.target.value })}
              />
              {editing?.assignedVehicle && (
                <p className="text-xs text-muted-foreground">
                  This machine is assigned to {editing.assignedVehicle} -- saving will update that vehicle's EFT ID too.
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_provider">Machine Provider</Label>
              <Input
                id="edit_provider"
                value={editValues.provider}
                onChange={(e) => setEditValues({ ...editValues, provider: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_monthly_rental">Rental / Month ($)</Label>
                <Input
                  id="edit_monthly_rental"
                  type="number"
                  step="0.01"
                  value={editValues.monthly_rental}
                  onChange={(e) => setEditValues({ ...editValues, monthly_rental: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_network_fee">Network Fee ($)</Label>
                <Input
                  id="edit_network_fee"
                  type="number"
                  step="0.01"
                  value={editValues.network_fee}
                  onChange={(e) => setEditValues({ ...editValues, network_fee: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_network_provider">Network Provider</Label>
              <Input
                id="edit_network_provider"
                value={editValues.network_provider}
                onChange={(e) => setEditValues({ ...editValues, network_provider: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editValues.status}
                onValueChange={(val) => setEditValues({ ...editValues, status: val as EftposMachineStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editValues.notes}
                onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Vehicle Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign EFTPOS Machine</DialogTitle>
            <DialogDescription>
              Assign SN <strong>{assigning?.serial_number}</strong> to a vehicle.
              {assigning?.terminal_id
                ? " This will set that vehicle's EFT ID for matching."
                : " No Terminal ID on file yet, so the vehicle's EFT ID won't be set."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="vehicle-select">Vehicle</Label>
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger id="vehicle-select">
                <SelectValue placeholder="Select a vehicle..." />
              </SelectTrigger>
              <SelectContent>
                {vehicleNumbers.map((vn) => {
                  const existing = machineByVehicle.get(vn);
                  const isOtherMachine = existing && existing.id !== assigning?.id;
                  return (
                    <SelectItem key={vn} value={vn}>
                      <div className="flex items-center gap-2">
                        <span>{vn}</span>
                        {isOtherMachine && (
                          <Badge variant="secondary" className="text-xs">
                            Has SN {existing!.serial_number}
                          </Badge>
                        )}
                        {existing?.id === assigning?.id && (
                          <Badge variant="outline" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={assignMachine} disabled={!selectedVehicle}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EftposMachinesTab;
